import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const FEATURES = [
  ['Users', 'Lead-to-job revenue workflow', 'Keep customer details, job information and lead status together so every opportunity has a clear next step.'],
  ['Calculator', 'Fence estimating and pricing', 'Build estimates using footage, gates and tear-out pricing. Maintain a reusable price book for your fence materials.'],
  ['PhoneCall', 'Quote Rescue follow-up', 'See due quote value, prepare the next customer draft, log calls and notes, and keep every follow-up date visible.'],
  ['HardHat', 'Work orders and production', 'Move accepted work into production with job scope, crew notes, material requirements and work order status.'],
  ['CalendarDays', 'Crew and install scheduling', 'Assign crews and installation dates. Review unscheduled jobs and upcoming starts from one workspace.'],
  ['CircleDollarSign', 'Invoice and payment records', 'Track invoices, balances and recorded payments alongside the work they relate to.'],
];
const TOUR_STEPS = [
  {
    label: 'Capture', icon: 'Users', title: 'Start with the whole picture.',
    description: 'Keep the customer, fence scope and site details together before you price the work.',
    stage: 'New lead', next: 'Review measurements and prepare an estimate',
    details: [['Project', 'Backyard privacy fence'], ['Scope', '180 ft · cedar · 2 gates'], ['Site note', 'Remove existing fence; keep driveway access clear']],
  },
  {
    label: 'Estimate', icon: 'Calculator', title: 'Turn scope into a clear quote.',
    description: 'Use your price book for footage, gates and tear-out, then prepare proposal options for the customer.',
    stage: 'Estimate prepared', next: 'Present the proposal and track the next follow-up',
    details: [['Fence', '180 ft of cedar privacy fence'], ['Gates', '2 walk gates'], ['Included work', 'Existing fence tear-out']],
  },
  {
    label: 'Schedule', icon: 'CalendarDays', title: 'Give accepted work a plan.',
    description: 'Choose an installation date and crew in Jobs so Production has the scheduling context it needs.',
    stage: 'Job scheduled', next: 'Review the work order before the crew starts',
    details: [['Install', 'Date assigned in Jobs'], ['Crew', 'Crew assigned to this job'], ['Handoff', 'Scope, access and material notes']],
  },
  {
    label: 'Build', icon: 'HardHat', title: 'Keep the jobsite in the loop.',
    description: 'Use Production to track work-order progress, with crew and material notes alongside the job.',
    stage: 'In progress', next: 'Update the work order as the installation progresses',
    details: [['Work order', 'Backyard privacy fence'], ['Crew note', 'Confirm gate swing with customer'], ['Material note', 'Cedar pickets and gate hardware']],
  },
];

const STARTING_POINTS = [
  {
    label: 'Prepare a quote',
    icon: 'Calculator',
    title: 'Build a quote you can explain.',
    description: 'Bring your fence scope and rates together before you present the price.',
    steps: [
      ['Set your rates', 'Review the materials, gate prices and tear-out rates in your price book.'],
      ['Capture the scope', 'Add the lead and record footage, gates and site details.'],
      ['Review the estimate', 'Check the quantities and assumptions, then prepare the customer proposal.'],
    ],
    to: '/estimates',
    action: 'Open estimates',
    takeaway: 'A reviewed estimate with a clear scope and next customer step.',
  },
  {
    label: 'Organize follow-ups',
    icon: 'PhoneCall',
    title: 'Know who needs a reply.',
    description: 'Give each open quote a next action so you can pick up the conversation with context.',
    steps: [
      ['Review open quotes', 'Find sent estimates and their planned follow-up dates.'],
      ['Choose the next action', 'Prepare a call, note or communication draft for the customer.'],
      ['Record the outcome', 'Log what happened and decide when to follow up again.'],
    ],
    to: '/follow-up',
    action: 'Open follow-ups',
    takeaway: 'A visible next action for each quote you review. Email and SMS remain drafts; sending is not automated.',
  },
  {
    label: 'Plan an install',
    icon: 'HardHat',
    title: 'Make the handoff clear.',
    description: 'Carry accepted scope into scheduling and production so the work starts with the right context.',
    steps: [
      ['Review accepted scope', 'Confirm the job details and outstanding customer questions.'],
      ['Schedule in Jobs', 'Assign the installation date and crew, then review the work order.'],
      ['Track work in Production', 'Keep crew and material notes together and update progress from the work order.'],
    ],
    to: '/jobs',
    action: 'Open Jobs',
    takeaway: 'A scheduled job and a work order with the details needed for production.',
  },
];

const QUESTIONS = [
  ['Who is PicketOps for?', 'PicketOps is a revenue operating system for fence contractors who want one place to manage leads, estimates, proposals, quote follow-ups, jobs, production and payment records.'],
  ['Can I price footage, gates and tear-out?', 'Yes. The price book and estimating tools support fence footage, gates and tear-out pricing so you can reuse your rates when preparing quotes.'],
  ['Does PicketOps have a free fence estimate calculator?', 'Yes. The free fence estimate calculator combines footage, your per-foot selling rate, gates, tear-out, other charges and optional tax, then lets you send the total into Profit Guard.'],
  ['Does PicketOps have a contractor profit margin calculator?', 'Yes. The free Profit Guard calculator compares selling price, entered direct costs, gross profit, gross margin, markup and a target-margin selling price.'],
  ['Does PicketOps automatically send emails and texts?', 'No. Email and SMS entries currently remain drafts. You can track due drafts and log completed calls and notes; an outbound messaging provider is not connected.'],
  ['Can I schedule crews and track materials?', 'Yes. Jobs support crew assignments and installation dates, while Production keeps work orders, crew notes and material requirements together.'],
  ['How much does PicketOps cost?', 'The Founding Contractor Program offers a 30-day free trial with no credit card required and a $49 monthly founding price locked for 12 months. Create your account and company profile, then contact support for operator activation and confirmation of your trial dates and plan terms. Signup does not start billing. Contact support to cancel.'],
];

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const workspaceTarget = isAuthenticated ? '/dashboard' : '/contractor/signup';
  const [tourIndex, setTourIndex] = useState(0);
  const tour = TOUR_STEPS[tourIndex];
  const [startIndex, setStartIndex] = useState(0);
  const startingPoint = STARTING_POINTS[startIndex];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuButton = useRef(null);
  const { hash } = useLocation();

  // Client-side navigation does not perform the browser's initial fragment scroll.
  // Wait for this page to mount before resolving a known public section.
  useEffect(() => {
    const section = hash.slice(1);
    if (!['features', 'pricing', 'questions', 'workflow-tour', 'free-tools', 'getting-started', 'main-content'].includes(section)) return;
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(section);
      if (!target) return;
      target.setAttribute('tabindex', '-1');
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
      target.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  const closeMobileMenu = event => {
    const link = event.target.closest('a');
    if (!link) return;
    setMobileMenuOpen(false);
    const href = link.getAttribute('href');
    if (href?.startsWith('#')) {
      requestAnimationFrame(() => {
        const target = document.getElementById(href.slice(1));
        if (!target) return;
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    }
  };

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:block focus:p-4 focus:bg-card focus:text-foreground">Skip to content</a>
      <header className="max-w-6xl mx-auto px-4 pt-20 pb-5 sm:py-5 sm:pr-32 md:pl-6 flex flex-wrap items-center justify-between gap-4">
        <Link to="/" aria-label="PicketOps home" className="flex min-w-0 items-center gap-3 rounded-md focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="size-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-heading text-xl font-bold">P</span>
          <span>
            <span className="block font-heading text-xl font-bold tracking-tight leading-none">PICKETOPS</span>
            <span className="hidden sm:block text-xs text-muted-foreground mt-1">Revenue OS for fence contractors.</span>
          </span>
        </Link>
        <Button
          ref={menuButton}
          variant="outline"
          className="lg:hidden min-h-11"
          aria-expanded={mobileMenuOpen}
          aria-controls="public-navigation"
          onClick={() => setMobileMenuOpen(open => !open)}
        >
          <ApperIcon name={mobileMenuOpen ? 'X' : 'Menu'} size={18} aria-hidden="true"/>
          {mobileMenuOpen ? 'Close menu' : 'Menu'}
        </Button>
        <nav
          id="public-navigation"
          aria-label="Public navigation"
          className={`${mobileMenuOpen ? 'flex' : 'hidden'} w-full flex-col gap-2 rounded-xl border border-border bg-card p-3 text-sm lg:flex lg:w-auto lg:flex-row lg:flex-wrap lg:items-center lg:gap-4 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0`}
          onClick={closeMobileMenu}
          onKeyDown={event => {
            if (event.key === 'Escape' && mobileMenuOpen) {
              event.preventDefault();
              setMobileMenuOpen(false);
              menuButton.current?.focus();
            }
          }}
        >
          <a href="#workflow-tour" className="hover:underline focus-visible:ring-2 focus-visible:ring-ring">How it works</a>
          <a href="#features" className="hover:underline focus-visible:ring-2 focus-visible:ring-ring">Features</a>
          <a href="#pricing" className="hover:underline focus-visible:ring-2 focus-visible:ring-ring">Pricing</a>
          <a href="#free-tools" className="hover:underline focus-visible:ring-2 focus-visible:ring-ring">Free tools</a>
          <a href="#questions" className="hover:underline focus-visible:ring-2 focus-visible:ring-ring">FAQ</a>
          <Button variant="ghost" asChild>
            <Link to={isAuthenticated ? '/dashboard' : '/contractor/login'}>
              {isAuthenticated ? 'Open workspace' : 'Sign in'}
            </Link>
          </Button>
        </nav>
      </header>
      <main id="main-content" tabIndex={-1}>
        <section className="max-w-7xl mx-auto grid lg:grid-cols-2 overflow-hidden rounded-3xl border border-border bg-card">
          <div className="relative overflow-hidden bg-sidebar text-sidebar-foreground p-6 sm:p-10 lg:p-14 flex flex-col justify-center">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/80">PicketOps · Office to field</p>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-[1.06] tracking-tight">Ready before<br/>the crew arrives.</h1>
            <p className="mt-6 max-w-lg text-base sm:text-lg leading-relaxed text-sidebar-foreground/85">Keep fence estimates, job schedules, crew notes and materials together. Give every installation a clear next step.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" asChild className="min-h-12">
                <Link to={workspaceTarget}>{isAuthenticated ? 'Open workspace' : 'Create account'}<ApperIcon name="ArrowRight" size={18}/></Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="min-h-12 border-sidebar-border bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-foreground">
                <Link to="/fence-contractor-tools">Explore free tools</Link>
              </Button>
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-sidebar-foreground/75">Company setup is followed by operator activation. Signup does not start billing; support confirms trial dates and plan terms.</p>
            <div className="mt-10 grid grid-cols-3 gap-3 border-t border-sidebar-border pt-6">
              {[['CalendarDays','Plan the day'],['Package','Review materials'],['ClipboardList','Prepare the crew']].map(([icon,label]) => (
                <div key={label} className="space-y-2">
                  <ApperIcon name={icon} size={22} className="text-primary" aria-hidden="true"/>
                  <p className="text-sm font-medium">{label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="picketops-workflow p-5 sm:p-8 lg:p-10 flex flex-col items-center justify-center">
            <p className="mb-5 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Field companion · Illustrative preview</p>
            <div className="w-full max-w-sm overflow-hidden rounded-[2rem] border-4 border-sidebar bg-background shadow-(--shadow-lg)">
              <div className="px-5 pt-6 pb-4 flex items-center justify-between gap-3">
                <div><p className="text-xs text-muted-foreground">Your contractor workspace</p><h2 className="mt-1 font-heading text-3xl font-bold">Today</h2></div>
                <span className="flex size-10 items-center justify-center rounded-full bg-secondary text-secondary-foreground"><ApperIcon name="HardHat" size={22} aria-hidden="true"/></span>
              </div>
              <div className="mx-3 mb-4 rounded-2xl border border-border bg-card overflow-hidden">
                <div className="border-b border-border p-4">
                  <p className="text-xs font-semibold text-muted-foreground">Sample work order · WO-1003</p>
                  <h3 className="mt-1 font-heading text-xl font-bold">Cedar privacy fence</h3>
                  <p className="mt-2 inline-block rounded-md bg-secondary px-2 py-1 text-xs text-secondary-foreground">Ready for review</p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex gap-3"><ApperIcon name="ClipboardList" size={20} className="shrink-0 text-highlight" aria-hidden="true"/><div><h4 className="text-sm font-semibold">Crew notes</h4><p className="mt-1 text-sm leading-relaxed text-muted-foreground">Confirm gate swing. Keep driveway access clear.</p></div></div>
                  <div className="flex gap-3"><ApperIcon name="Package" size={20} className="shrink-0 text-highlight" aria-hidden="true"/><div><h4 className="text-sm font-semibold">Material review</h4><p className="mt-1 text-sm leading-relaxed text-muted-foreground">Posts, cedar pickets and gate hardware. Check ordering status before work starts.</p></div></div>
                  <a href="#workflow-tour" className="flex min-h-12 items-center justify-center gap-2 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring">Explore the crew workflow<ApperIcon name="ArrowDown" size={16} aria-hidden="true"/></a>
                </div>
              </div>
              <div aria-label="Example mobile navigation" className="grid grid-cols-4 border-t border-border bg-card px-2 py-3">
                {[['LayoutDashboard','Today'],['Briefcase','Jobs'],['HardHat','Production'],['User','Account']].map(([icon,label]) => (
                  <div key={label} className="flex flex-col items-center gap-1 text-muted-foreground"><ApperIcon name={icon} size={18} aria-hidden="true"/><span className="text-[11px]">{label}</span></div>
                ))}
              </div>
            </div>
            <p className="mt-4 max-w-sm text-center text-xs leading-relaxed text-muted-foreground">Sample details only. Your signed-in workspace shows your own records. Review handoff text before copying or sharing it.</p>
          </div>
        </section>
        <section id="workflow-tour" aria-labelledby="tour-heading" className="max-w-6xl mx-auto px-4 md:px-6 pt-14 md:pt-20 scroll-mt-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.16em] font-semibold text-primary mb-3">Take a closer look</p>
              <h2 id="tour-heading" className="text-3xl md:text-4xl font-heading font-bold">One job. A clear next step.</h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-sm">Explore an illustrative fence project. This tour uses sample details and does not create any records.</p>
          </div>
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-(--shadow-md)">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 bg-muted/40 border-b border-border" role="group" aria-label="Workflow stages">
              {TOUR_STEPS.map((step, index) => (
                <button
                  key={step.label}
                  type="button"
                  aria-pressed={tourIndex === index}
                  aria-controls="workflow-example"
                  onClick={() => setTourIndex(index)}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tourIndex === index ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
                >
                  <ApperIcon name={step.icon} size={18} aria-hidden="true"/>
                  {index + 1}. {step.label}
                </button>
              ))}
            </div>
            <div id="workflow-example" aria-live="polite" aria-atomic="true" className="grid md:grid-cols-2">
              <div className="p-6 md:p-9 flex flex-col justify-between gap-8">
                <div>
                  <span className="inline-flex items-center justify-center size-14 rounded-2xl bg-primary/10 text-primary mb-6">
                    <ApperIcon name={tour.icon} size={28} aria-hidden="true"/>
                  </span>
                  <p className="text-xs uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-3">Step {tourIndex + 1} of 4</p>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold">{tour.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-4">{tour.description}</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" disabled={tourIndex === 0} onClick={() => setTourIndex(index => Math.max(0, index - 1))}>
                    <ApperIcon name="ArrowLeft" size={16}/>Previous step
                  </Button>
                  <Button onClick={() => setTourIndex(index => (index + 1) % TOUR_STEPS.length)}>
                    {tourIndex === 3 ? 'Restart tour' : 'Next step'}<ApperIcon name={tourIndex === 3 ? 'RotateCcw' : 'ArrowRight'} size={16}/>
                  </Button>
                </div>
              </div>
              <div className="bg-muted/35 border-t md:border-t-0 md:border-l border-border p-5 md:p-9">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Illustrative project</span>
                  <span className="rounded-full border border-primary/20 bg-primary/10 text-primary text-xs px-3 py-1.5">{tour.stage}</span>
                </div>
                <div className="rounded-xl bg-background border border-border p-5 shadow-sm">
                  <div className="flex items-center gap-3 pb-5 border-b border-border">
                    <span className="size-10 flex items-center justify-center rounded-lg bg-primary text-primary-foreground"><ApperIcon name="Fence" size={22} aria-hidden="true"/></span>
                    <div><p className="font-heading text-lg font-bold">Cedar privacy project</p><p className="text-xs text-muted-foreground">Sample workflow · no live customer data</p></div>
                  </div>
                  <dl className="divide-y divide-border">
                    {tour.details.map(([label, value]) => (
                      <div key={label} className="py-4">
                        <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
                        <dd className="mt-1 text-sm font-medium">{value}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="rounded-lg bg-primary/5 border border-primary/15 p-4 mt-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">Next action</p>
                    <p className="text-sm mt-2">{tour.next}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="features" aria-labelledby="features-heading" className="border-y border-border bg-card/50 scroll-mt-6 mt-12">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-20">
            <p className="text-xs uppercase tracking-[0.16em] font-semibold text-primary mb-3">One workspace. Every stage.</p>
            <h2 id="features-heading" className="text-3xl md:text-4xl font-heading font-bold max-w-2xl">Built around your work.<br/>From the office to the jobsite.</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
              {FEATURES.map(([icon, title, description]) => (
                <Card key={title} className="shadow-none rounded-xl border-border/80">
                  <CardContent className="p-6">
                    <span className="inline-flex size-11 items-center justify-center rounded-xl bg-primary/10"><ApperIcon name={icon} className="text-primary" aria-hidden="true"/></span>
                    <h3 className="font-heading text-xl font-bold mt-5">{title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-2">{description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        <section id="pricing" aria-labelledby="pricing-heading" className="max-w-6xl mx-auto px-4 md:px-6 py-12 scroll-mt-6">
          <div className="mb-8 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.16em] font-semibold text-primary mb-3">Founding Contractor Program</p>
            <h2 id="pricing-heading" className="text-3xl md:text-4xl font-heading font-bold">Try it on the work you already have.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">Create your account, save your company profile, then contact support to request activation. Once your trial dates are confirmed, start with one lead, one estimate or one upcoming install.</p>
          </div>
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] rounded-2xl border border-primary/25 bg-card overflow-hidden shadow-(--shadow-md)">
            <div className="p-6 md:p-9 bg-primary/5 flex flex-col items-start">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary"><ApperIcon name="CalendarDays" size={15} aria-hidden="true"/>30-day free trial</span>
              <h3 className="mt-6 text-xl font-heading font-bold">Your contractor workspace</h3>
              <p className="mt-4 flex flex-wrap items-baseline gap-x-2"><span className="text-6xl font-bold tracking-tight tabular-nums">$49</span><span className="text-sm text-muted-foreground">/ month after your trial</span></p>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">Operator activation required. No credit card is required for the trial. Support confirms your trial dates and 12-month founding price lock before activation. Contact support to cancel.</p>
              <Button size="lg" asChild className="mt-7 w-full whitespace-normal h-auto min-h-12 py-3 text-center">
                <Link to={workspaceTarget}>{isAuthenticated ? 'Open your dashboard' : 'Create contractor account'}<ApperIcon name="ArrowRight" className="shrink-0" aria-hidden="true"/></Link>
              </Button>
              <a href="#getting-started" className="mt-4 inline-flex items-center gap-2 min-h-11 text-sm font-medium underline underline-offset-4 rounded focus-visible:ring-2 focus-visible:ring-ring">Choose your first task<ApperIcon name="ArrowDown" size={16} aria-hidden="true"/></a>
            </div>
            <div className="p-6 md:p-9 border-t lg:border-t-0 lg:border-l border-border">
              <h3 className="text-xl font-heading font-bold">What you can organize</h3>
              <ul className="mt-5 space-y-4">
                {[
                  ['Leads and customer context', 'Keep contact details and job scope ready for the next conversation.'],
                  ['Estimates and proposals', 'Use your price book for footage, gates and tear-out.'],
                  ['Scheduling and production', 'Plan installations in Jobs and track field progress in Production.'],
                  ['Follow-ups and payment records', 'Review next actions, invoices, balances and recorded payments.'],
                ].map(([title, description]) => (
                  <li key={title} className="flex gap-3">
                    <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><ApperIcon name="Check" size={15} aria-hidden="true"/></span>
                    <div><p className="text-sm font-semibold">{title}</p><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{description}</p></div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-t border-border pt-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Know before you start</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Email and SMS entries are drafts; sending is not automated.</p>
                <Link to="/fence-estimate-checklist" className="mt-2 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-primary underline underline-offset-4 rounded focus-visible:ring-2 focus-visible:ring-ring">Not ready for an account? Try the free checklist<ApperIcon name="ArrowRight" size={16} className="shrink-0" aria-hidden="true"/></Link>
              </div>
            </div>
          </div>
        </section>
        <section id="free-tools" aria-labelledby="resource-heading" className="max-w-6xl mx-auto px-4 md:px-6 pb-12 scroll-mt-6">
          <div className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 p-6 md:p-9 shadow-(--shadow-md)">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div className="max-w-2xl">
                <p className="text-xs uppercase tracking-[0.16em] font-semibold text-primary">Free contractor revenue toolbox</p>
                <h2 id="resource-heading" className="mt-3 text-3xl md:text-4xl font-heading font-bold tracking-tight">Find the leak. Fix the next step.</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Use the same practical resources behind the PicketOps Revenue OS before you ever create an account.</p>
              </div>
              <Button variant="outline" asChild><Link to="/fence-contractor-tools">View all free tools <ApperIcon name="ArrowRight" size={16}/></Link></Button>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-8">
              {[
                {
                  icon: 'Gauge',
                  eyebrow: '3-minute assessment',
                  title: 'Contractor revenue scorecard',
                  description: 'Score lead response, quote follow-up, pricing discipline, reviews and referrals—then see the weakest stage.',
                  to: '/contractor-revenue-scorecard',
                  action: 'Check my revenue leaks',
                },
                {
                  icon: 'Ruler',
                  eyebrow: 'Field measurements',
                  title: 'Fence measurement worksheet',
                  description: 'Break a fence project into runs, total footage, track heights, gates, tear-out and site notes.',
                  to: '/fence-measurement-worksheet',
                  action: 'Measure the job',
                },
                {
                  icon: 'Boxes',
                  eyebrow: 'Material takeoff',
                  title: 'Fence material takeoff calculator',
                  description: 'Estimate sections, posts, rails, pickets and optional concrete from your own layout assumptions.',
                  to: '/fence-material-takeoff-calculator',
                  action: 'Plan material quantities',
                },
                {
                  icon: 'Calculator',
                  eyebrow: 'Free quote math',
                  title: 'Fence estimate calculator',
                  description: 'Calculate selling price from footage, gates, tear-out, other charges and optional tax using your own rates.',
                  to: '/fence-estimate-calculator',
                  action: 'Calculate an estimate',
                },
                {
                  icon: 'FilePenLine',
                  eyebrow: 'Scope builder',
                  title: 'Fence estimate scope builder',
                  description: 'Turn footage, gates, removal, inclusions, exclusions and site notes into a copy-ready scope of work.',
                  to: '/fence-estimate-template',
                  action: 'Build the quote scope',
                },
                {
                  icon: 'Columns3',
                  eyebrow: 'Proposal options',
                  title: 'Fence proposal option builder',
                  description: 'Compare Good, Better and Best-style choices using your own materials, scope and selling prices.',
                  to: '/fence-proposal-option-builder',
                  action: 'Build proposal options',
                },
                {
                  icon: 'Users',
                  eyebrow: 'Crew planning',
                  title: 'Fence labor & crew planner',
                  description: 'Convert footage and your productivity assumptions into crew-days, worker-hours and optional direct labor cost.',
                  to: '/fence-labor-crew-planner',
                  action: 'Plan crew labor',
                },
                {
                  icon: 'CalendarDays',
                  eyebrow: 'Install scheduling',
                  title: 'Fence installation schedule planner',
                  description: 'Turn crew workdays into working start, production finish and buffered planning dates.',
                  to: '/fence-install-schedule-planner',
                  action: 'Plan install window',
                },
                {
                  icon: 'FileDiff',
                  eyebrow: 'Change management',
                  title: 'Fence change order planner',
                  description: 'Model additions or credits, revised contract value, internal cost impact and schedule changes.',
                  to: '/fence-change-order-planner',
                  action: 'Plan a change order',
                },
                {
                  icon: 'ClipboardCheck',
                  eyebrow: 'Project closeout',
                  title: 'Fence final walkthrough & closeout',
                  description: 'Review finished work, punch-list items, customer handoff and final billing readiness before closing the job.',
                  to: '/fence-final-walkthrough-checklist',
                  action: 'Run final walkthrough',
                },
                {
                  icon: 'ShieldCheck',
                  eyebrow: 'Profit Guard',
                  title: 'Fence profit margin calculator',
                  description: 'Compare selling price, direct costs, gross margin, markup and the price required to hit your target margin.',
                  to: '/fence-profit-margin-calculator',
                  action: 'Run a profit check',
                },
                {
                  icon: 'MessagesSquare',
                  eyebrow: '5-touch sequence',
                  title: 'Fence estimate follow-up guide',
                  description: 'Use copy-ready day 1, 3, 7, 14 and 30 follow-up scripts without sounding pushy or inventing urgency.',
                  to: '/fence-estimate-follow-up',
                  action: 'Open the follow-up guide',
                },
                {
                  icon: 'ClipboardCheck',
                  eyebrow: '6 review areas',
                  title: 'Fence estimate checklist',
                  description: 'Review measurements, gates, site conditions, pricing assumptions and open questions before sending the quote.',
                  to: '/fence-estimate-checklist',
                  action: 'Open the estimate checklist',
                },
              ].map(resource => (
                <Link key={resource.title} to={resource.to} className="group flex min-h-64 flex-col rounded-xl border border-border bg-background p-5 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-(--shadow-md) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span className="flex size-11 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name={resource.icon} size={21} aria-hidden="true"/></span>
                  <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{resource.eyebrow}</p>
                  <h3 className="mt-2 text-xl font-heading font-semibold">{resource.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{resource.description}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">{resource.action}<ApperIcon name="ArrowRight" size={16} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true"/></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
        <section id="getting-started" aria-labelledby="getting-started-heading" className="max-w-6xl mx-auto px-4 md:px-6 pb-16 scroll-mt-6">
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-(--shadow-md)">
            <div className="p-6 md:p-9 border-b border-border">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Make your first session useful</p>
              <h2 id="getting-started-heading" className="mt-3 text-3xl md:text-4xl font-heading font-bold">What would you like to organize first?</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground max-w-2xl">Choose the work on your desk today. Get a practical starting plan before creating your contractor account.</p>
              <div role="group" aria-label="Choose a starting workflow" className="grid sm:grid-cols-3 gap-3 mt-6">
                {STARTING_POINTS.map((point, index) => (
                  <button
                    key={point.label}
                    type="button"
                    aria-pressed={startIndex === index}
                    aria-controls="starting-plan"
                    onClick={() => setStartIndex(index)}
                    className={`flex items-center gap-3 rounded-xl border p-4 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${startIndex === index ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-accent hover:text-accent-foreground'}`}
                  >
                    <ApperIcon name={point.icon} size={20} aria-hidden="true"/>
                    {point.label}
                  </button>
                ))}
              </div>
            </div>
            <div id="starting-plan" className="grid lg:grid-cols-2">
              <div className="p-6 md:p-9 bg-muted/30">
                <div aria-live="polite" aria-atomic="true">
                  <p className="text-xs uppercase tracking-wide font-semibold text-primary">Your starting plan</p>
                  <h3 className="mt-3 text-2xl font-heading font-bold">{startingPoint.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{startingPoint.description}</p>
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mt-6">
                    <p className="text-sm font-semibold">What you are working toward</p>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{startingPoint.takeaway}</p>
                  </div>
                </div>
                <Button asChild className="mt-6">
                  <Link to={isAuthenticated ? startingPoint.to : '/contractor/signup'}>
                    {isAuthenticated ? startingPoint.action : 'Create contractor account'}
                    <ApperIcon name="ArrowRight" size={16}/>
                  </Link>
                </Button>
                <p className="mt-3 text-xs text-muted-foreground">Request operator activation after company setup. The founding offer is 30 days free, then $49/month; support confirms your dates and plan terms. Signup does not start billing.</p>
              </div>
              <ol className="p-6 md:p-9 space-y-6" aria-label={startingPoint.label + ' steps'}>
                {startingPoint.steps.map(([title, description], index) => (
                  <li key={title} className="flex gap-4">
                    <span aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/5 text-primary text-sm font-bold">{index + 1}</span>
                    <div>
                      <h4 className="font-semibold">{title}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-1">{description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
        <section id="questions" aria-labelledby="questions-heading" className="max-w-4xl mx-auto px-4 md:px-6 pb-12 scroll-mt-6">
          <h2 id="questions-heading" className="text-2xl font-heading font-semibold mb-6">Questions about PicketOps</h2>
          <div className="space-y-3">
            {QUESTIONS.map(([question, answer]) => (
              <details key={question} className="group rounded-md border border-border bg-card">
                <summary className="cursor-pointer rounded-md px-5 py-4 text-base font-semibold hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {question}
                </summary>
                <p className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
      <footer className="border-t border-border bg-card">
        <div className="max-w-6xl mx-auto px-5 md:px-6 pt-10 pb-24 md:pt-14 md:pb-24">
          <div className="grid gap-8 md:grid-cols-[1.4fr_1fr_1fr] md:gap-12">
            <div>
              <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span aria-hidden="true" className="size-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-heading text-xl font-bold">P</span>
                <span className="font-heading text-xl font-bold tracking-tight">PICKETOPS</span>
              </Link>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">The revenue operating system for fence contractors. Find the leaks between lead, quote, follow-up, job and payment—and keep the next action visible.</p>
              <Link to={isAuthenticated ? '/dashboard' : '/contractor/login'} className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-md text-sm font-semibold text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {isAuthenticated ? 'Open your workspace' : 'Contractor sign in'}
                <ApperIcon name="ArrowRight" size={16} aria-hidden="true"/>
              </Link>
            </div>
            <nav aria-labelledby="footer-explore-heading">
              <h2 id="footer-explore-heading" className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Explore PicketOps</h2>
              <ul className="mt-3 space-y-1">
                {[
                  ['#workflow-tour', 'See the workflow'],
                  ['#features', 'Contractor software features'],
                  ['#pricing', 'Plans and pricing'],
                  ['#free-tools', 'Free contractor tools'],
                  ['#questions', 'Common questions'],
                ].map(([href, label]) => (
                  <li key={href}><a href={href} className="inline-flex min-h-11 items-center rounded-md text-sm hover:text-primary hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{label}</a></li>
                ))}
              </ul>
            </nav>
            <nav aria-labelledby="footer-start-heading">
              <h2 id="footer-start-heading" className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Start with a useful next step</h2>
              <Link to="/contractor-revenue-scorecard" className="mt-4 flex w-full flex-col items-start rounded-xl border border-primary/20 bg-primary/5 p-4 transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="flex items-start gap-3 text-sm font-semibold"><ApperIcon name="Gauge" size={20} className="shrink-0 text-primary" aria-hidden="true"/>Free contractor revenue scorecard</span>
                <span className="mt-2 block text-xs leading-relaxed text-muted-foreground">Find the weakest stage in your lead-to-job process and get a practical next action. No account needed.</span>
              </Link>
              <Link to="/fence-contractor-tools" className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md text-sm hover:text-primary hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Browse all free contractor tools<ApperIcon name="ArrowRight" size={16} aria-hidden="true"/></Link>
              <Link to="/fence-contractor-software" className="inline-flex min-h-11 items-center gap-2 rounded-md text-sm hover:text-primary hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Fence contractor software guide<ApperIcon name="ArrowRight" size={16} aria-hidden="true"/></Link>
              <Link to="/fence-estimate-checklist" className="inline-flex min-h-11 items-center gap-2 rounded-md text-sm hover:text-primary hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Use the fence estimate checklist<ApperIcon name="ArrowRight" size={16} aria-hidden="true"/></Link>
            </nav>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-border pt-5">
            <p className="text-xs text-muted-foreground">PicketOps · Built for fence contractors.</p>
            <a href="#main-content" className="inline-flex min-h-11 items-center gap-2 rounded-md text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Back to top<ApperIcon name="ArrowUp" size={16} aria-hidden="true"/></a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export const route = { path: '/', layout: 'public' };
